/**
 * Registry des enregistrements branding, + résolution de la cascade
 * multi-établissement (`parentTenantId`).
 *
 * Les données vivent dans `tenants/<id>/branding.json`. Elles sont importées
 * statiquement (bundlées par Vite, un JSON par tenant) pour un lookup synchrone
 * au boot, sans fetch réseau.
 */
import type { FeatureFlags, TenantBranding } from './types';

import saufImprevu from '../tenants/sauf-imprevu/branding.json';
import maisonsBocuse from '../tenants/maisons-bocuse/branding.json';
import mayday from '../tenants/mayday/branding.json';

/** Enregistrements branding par id tenant. */
export const registry: Record<string, TenantBranding> = {
  'sauf-imprevu': saufImprevu as TenantBranding,
  'maisons-bocuse': maisonsBocuse as TenantBranding,
  'mayday': mayday as TenantBranding,
};

/**
 * Alias sous-domaine/host → id tenant. Construit depuis chaque `domains[]`,
 * plus l'`id` lui-même. Étendre ici pour les hosts custom.
 */
export const domainMap: Record<string, string> = buildDomainMap(registry);

function buildDomainMap(reg: Record<string, TenantBranding>): Record<string, string> {
  const map: Record<string, string> = {};
  for (const [id, b] of Object.entries(reg)) {
    map[id] = id;
    for (const d of b.domains ?? []) map[d.toLowerCase()] = id;
  }
  return map;
}

/** Fusionne deux maps de feature flags (l'enfant l'emporte). */
function mergeFeatures(parent: FeatureFlags, child: FeatureFlags): FeatureFlags {
  return { ...parent, ...child };
}

/**
 * Fusion parent → enfant pour la cascade multi-établissement.
 * L'enfant hérite du parent puis surcharge : `cssVars`, `features`, `pwa` et
 * `assets` sont deep-mergés ; les scalaires de l'enfant l'emportent s'ils sont
 * définis.
 */
export function mergeBranding(parent: TenantBranding, child: TenantBranding): TenantBranding {
  return {
    ...parent,
    ...child,
    displayName: child.displayName ?? parent.displayName,
    shortName: child.shortName ?? parent.shortName,
    tagline: child.tagline ?? parent.tagline,
    founder: child.founder ?? parent.founder,
    website: child.website ?? parent.website,
    fontFamily: child.fontFamily ?? parent.fontFamily,
    cssVars: { ...parent.cssVars, ...child.cssVars },
    features: mergeFeatures(parent.features ?? {}, child.features ?? {}),
    assets: { ...parent.assets, ...child.assets },
    establishment: { ...parent.establishment, ...child.establishment },
    pwa: { ...parent.pwa, ...child.pwa },
    domains: child.domains ?? parent.domains,
  };
}

/**
 * Renvoie l'enregistrement branding résolu d'un tenant, cascade parent
 * appliquée. Tenant inconnu → fallback sur le tenant par défaut.
 */
export function resolveBranding(tenantId: string, fallbackTenant = 'sauf-imprevu'): TenantBranding {
  const base = registry[tenantId] ?? registry[fallbackTenant];
  if (!base) {
    throw new Error(
      `[module-branding] Aucun branding pour "${tenantId}" ni fallback "${fallbackTenant}".`,
    );
  }
  if (base.parentTenantId && registry[base.parentTenantId]) {
    return mergeBranding(registry[base.parentTenantId], base);
  }
  return base;
}

/** Résout la valeur d'un feature flag (défaut `false`). */
export function featureEnabled(
  features: FeatureFlags,
  key: string,
  defaultValue = false,
): boolean {
  return features[key] ?? defaultValue;
}
